/**
 * RFC 7807 (Problem Details for HTTP APIs) 準拠エラーレスポンス型
 */
export interface ApiErrorResponse {
  /** RFC 7807: エラー種別 URL */
  type?: string;
  /** RFC 7807: エラータイトル */
  title?: string;
  /** RFC 7807: HTTP ステータスコード */
  status?: number;
  /** RFC 7807: エラー詳細メッセージ */
  detail?: string;
  /** マシンリーダブルなエラーコード */
  errorCode?: string;
  /** リトライ可能か */
  isRetryable?: boolean;
  /** タイムスタンプ (ISO 8601 UTC) */
  timestamp?: string;
  /** X-Ray Trace ID */
  traceId?: string;
  /** 追加コンテキスト */
  context?: Record<string, unknown>;
  /** バリデーションエラー詳細 */
  details?: {
    validation_errors?: Array<{
      row: number;
      errors: string[];
      data?: unknown;
    }>;
    [key: string]: unknown;
  };
  /** 後方互換: エラーメッセージ */
  error?: string;
  /** 後方互換: エラーコード */
  error_code?: string;
}

/**
 * AxiosError からエラーレスポンスを安全に抽出する
 */
/** 文字列プロパティを安全に読む（存在しない・文字列でない場合は undefined）。 */
const readString = (source: object, key: string): string | undefined => {
  if (!(key in source)) return undefined;
  const value = Reflect.get(source, key);
  return typeof value === 'string' ? value : undefined;
};

/**
 * レスポンスボディを `ApiErrorResponse` として読む。
 *
 * ボディはサーバ由来なので構造は実行時にしか分からない。`as ApiErrorResponse` で黙らせると、
 * 形が違ったときに `errorCode` などが undefined のまま流れて分岐が静かに変わる（AGENTS.md 1）。
 * 既知のフィールドだけを文字列として取り出す。
 */
const readApiErrorResponse = (data: unknown): ApiErrorResponse | null => {
  if (typeof data !== 'object' || data === null) return null;
  return {
    errorCode: readString(data, 'errorCode'),
    error_code: readString(data, 'error_code'),
    detail: readString(data, 'detail'),
    error: readString(data, 'error'),
  };
};

export const parseApiError = (error: unknown): ApiErrorResponse | null => {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    return readApiErrorResponse(error.response.data);
  }
  return null;
};

